'use client';

import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function BotoxPreise2026PageDE() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/de/blog/botox-preise-2026';
  const pageTitle = 'Botox Preise 2026 - Wie viel kostet es, wovon hängt es ab?';
  const pageDescription = 'Vollständiger Leitfaden zu Botox-Preisen 2026. Faktoren, die Botox-Kosten beeinflussen, detaillierte Preisliste und was Sie vor einer Botox-Behandlung wissen müssen.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Startseite', href: '/de' },
              { label: 'Blog', href: '/de/blog' },
              { label: 'Botox Preise 2026', href: '/de/blog/botox-preise-2026' },
            ]}
          />
        </div>

        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Botox Preise 2026 Leitfaden und Behandlungsinformationen"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Ästhetische Behandlungen
              </span>
            </div>
          </div>

          <div className="p-6 sm:p-8">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Botox Preise 2026 - Wie viel kostet es, wovon hängt es ab?
            </h1>

            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Baris Cin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>15. Januar 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>8 Minuten Lesezeit</span>
              </div>
            </div>

            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Teilen:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Eine der meistgestellten Fragen von Menschen, die über Botox nachdenken: "Wie hoch sind die Botox-Preise 2026?" In diesem Artikel besprechen wir ausführlich die aktuellen Preise, den Anwendungsprozess und die Wirkung.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Was ist Botox und welche Vorteile bietet es?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Botulinum-Toxin (Botox) ist eine medizinisch-ästhetische Anwendung, die Muskeln vorübergehend lähmt und dadurch das Erscheinungsbild von Falten reduziert. Am häufigsten wird es bei Stirnfalten, Augenfalten (Krähenfüße) und Zornesfalten angewendet.
              </p>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Es ist auch wirksam bei der Behandlung medizinischer Probleme wie übermäßigem Schwitzen (Hyperhidrose), Zähneknirschen (Bruxismus) und Migräne.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Wie wird Botox angewendet?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Die Botox-Anwendung dauert durchschnittlich 10-15 Minuten. Mit feinen Nadeln wird es in die Zielmuskeln injiziert. Eine Betäubung ist nicht erforderlich, nach der Anwendung kann die Person ihr tägliches Leben fortsetzen.
              </p>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <h3 className="text-lg font-semibold text-blue-900 mb-3">Häufigste Anwendungsbereiche:</h3>
                <ul className="list-disc list-inside text-blue-800 space-y-2">
                  <li>Stirn</li>
                  <li>Zornesfalten</li>
                  <li>Augenbereich</li>
                  <li>Nasenbereich</li>
                  <li>Oberlippe (Gummy Smile)</li>
                  <li>Halsbereich</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Wie lange hält die Botox-Wirkung an?
              </h2>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Die ersten Effekte sind nach 3-4 Tagen sichtbar, die vollständigen Ergebnisse zeigen sich nach 10-14 Tagen. Die Wirkung hält durchschnittlich 4-6 Monate an. Bei regelmäßiger Anwendung kann die Dauerhaftigkeit zunehmen.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Botox Preise 2026
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Die Botox-Preise variieren je nach Anzahl der zu behandelnden Bereiche, Klinikqualität, Arztexpertise und verwendeter Produktmarke. In der folgenden Tabelle können Sie die Durchschnittspreise sehen:
              </p>

              <div className="overflow-x-auto mb-8">
                <table className="w-full bg-white border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Anwendungsbereich
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Durchschnittspreis (€)
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900">Stirn</td>
                      <td className="px-6 py-4 text-sm text-gray-900 font-semibold">200 - 350</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">Zornesfalten</td>
                      <td className="px-6 py-4 text-sm text-gray-900 font-semibold">180 - 300</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900">Augenbereich (Krähenfüße)</td>
                      <td className="px-6 py-4 text-sm text-gray-900 font-semibold">180 - 300</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">Drei Bereiche (Kombi-Paket)</td>
                      <td className="px-6 py-4 text-sm text-gray-900 font-semibold">450 - 700</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900">Übermäßiges Schwitzen (Achseln)</td>
                      <td className="px-6 py-4 text-sm text-gray-900 font-semibold">500 - 800</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-8">
                <div className="flex">
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      <strong>💡 Hinweis:</strong> Die Preise sind Durchschnittspreise privater Kliniken in Großstädten wie Berlin, München, Hamburg. In öffentlichen Krankenhäusern wird Botox für ästhetische Zwecke nicht durchgeführt.
                    </p>
                  </div>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Was vor und nach der Botox-Behandlung zu beachten ist
              </h2>
              
              <div className="bg-red-50 rounded-xl p-6 mb-6">
                <h3 className="text-lg font-semibold text-red-900 mb-3">Was nicht getan werden sollte:</h3>
                <ul className="list-disc list-inside text-red-800 space-y-2">
                  <li>Am Behandlungstag keinen Sport treiben</li>
                  <li>Mindestens 4 Stunden nicht auf dem Bauch liegen</li>
                  <li>Die ersten 24 Stunden Sauna und Dampfbad meiden</li>
                  <li>Blutverdünnende Medikamente vorher absetzen (mit Arztgenehmigung)</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Häufig gestellte Fragen
              </h2>

              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">1. Ist die Beratungsgebühr im Botox-Preis enthalten?</h3>
                  <p className="text-gray-700">Normalerweise nicht. Kliniken können die erste Beratung kostenpflichtig durchführen (50-100€ Bereich).</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">2. Können auch Männer Botox bekommen?</h3>
                  <p className="text-gray-700">Ja. Stirn- und Zornesfalten-Botox ist bei Männern sehr verbreitet.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">3. Ist Botox schädlich?</h3>
                  <p className="text-gray-700">Bei angemessener Dosierung und Anwendung durch einen Facharzt ist es sicher. Nebenwirkungen sind meist vorübergehend (Rötung, Blutergüsse usw.).</p>
                </div>
              </div>

              <div className="relative h-64 rounded-xl overflow-hidden mb-8">
                <Image
                  src="/images/terleme-tedavisi.webp"
                  alt="Stirn-Botox-Anwendung"
                  fill
                  className="object-cover"
                />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Quellen
              </h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-8">
                <li>Deutsche Dermatologische Gesellschaft – <a href="https://www.derma.de" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://www.derma.de</a></li>
                <li>American Academy of Dermatology – <a href="https://www.aad.org" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://www.aad.org</a></li>
                <li>Vereinigung der Deutschen Ästhetisch-Plastischen Chirurgen</li>
                <li>2026 Klinik-Preisrecherche (12 private Kliniken in Berlin, München, Hamburg untersucht)</li>
              </ul>
            </div>

            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white text-center mt-12">
              <h3 className="text-2xl font-bold mb-4">Termin für Botox-Anwendung vereinbaren</h3>
              <p className="mb-6">Holen Sie sich detaillierte Informationen von unserem Facharzt und kontaktieren Sie uns sofort für ein individuelles Preisangebot.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  href={`https://wa.me/905438999586?text=${encodeURIComponent('Ich möchte Informationen über Botox-Anwendung erhalten.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                >
                  <FaWhatsapp className="w-5 h-5 mr-2" />
                  WhatsApp Kontakt
                </Link>
                <Link
                  href="/de/kontakt"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-blue-600 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Termin vereinbaren
                </Link>
              </div>
            </div>
          </div>
        </article>

        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Verwandte Artikel</h3>
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <p className="text-gray-600 mb-4">
              Bald werden wir mit weiteren informativen Inhalten hier sein.
            </p>
            <Link
              href="/de/blog"
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
            >
              Alle Blog-Artikel anzeigen
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
} 